import { useMemo } from 'react';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '@/config/googleMaps';
import type { DashboardMapPoint } from '@/types/dashboard';
import type { SalesByCityItem } from '@/utils/dashboardMap';
import { buildMapMarkers, sumMarkerOrders, sumMarkerRevenue } from '@/utils/dashboardMap';
import { useActiveMapMarker } from '../../hooks/useActiveMapMarker';
import { InsightCardHeader } from '../InsightCardHeader';
import { MapCanvas } from './MapCanvas';
import { MapSidebar } from './MapSidebar';
import styles from '../../Dashboard.module.scss';

interface OrdersMapProps {
  points: DashboardMapPoint[];
  salesByCity?: SalesByCityItem[];
}

export function OrdersMap({ points = [], salesByCity = [] }: OrdersMapProps) {
  const markers = useMemo(() => buildMapMarkers(points, salesByCity), [points, salesByCity]);

  const { activeMarker, activeKey, selectMarker } = useActiveMapMarker(markers);
  const totalOrders = sumMarkerOrders(markers);
  const totalRevenue = sumMarkerRevenue(markers);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'betiyan-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  return (
    <div className={styles.mapCard}>
      <InsightCardHeader
        icon={<EnvironmentOutlined />}
        title="Orders by location"
        subtitle="Click a pin or city to explore order distribution"
        badge={`${markers.length} cities · ${totalOrders} orders`}
      />

      <div className={styles.mapLayout}>
        <div className={styles.mapBody}>
          <MapCanvas
            markers={markers}
            activeKey={activeKey}
            isLoaded={isLoaded}
            loadError={loadError}
            onMarkerSelect={selectMarker}
          />
        </div>

        {markers.length > 0 ? (
          <MapSidebar
            markers={markers}
            activeKey={activeKey}
            activeMarker={activeMarker}
            totalOrders={totalOrders}
            totalRevenue={totalRevenue}
            onSelect={selectMarker}
          />
        ) : null}
      </div>
    </div>
  );
}
